import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaStar } from 'react-icons/fa';
import { getCollection, addDocument, updateDocument, deleteDocument } from '../../firebase/firestore';
import { uploadFile, deleteFile } from '../../firebase/storage';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import './AdminPages.css';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    company: '',
    message: '',
    rating: 5,
    image: '',
    active: true,
    order: 1
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const { db } = await import('../../firebase/config');

      if (!db) {
        setLoading(false);
        return;
      }

      const data = await getCollection('testimonials');
      setTestimonials(data.sort((a, b) => {
        if (b.createdAt && a.createdAt) {
          return b.createdAt.toMillis() - a.createdAt.toMillis();
        }
        return 0;
      }));
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500000) {
        alert('Image is too large. Please use an image under 500KB or enter an image URL instead.');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = { ...formData };

      // If a new image file was uploaded, try to upload it to Firebase Storage
      if (imageFile) {
        try {
          const timestamp = Date.now();
          const fileName = `testimonials/${timestamp}_${imageFile.name}`;
          const imageUrl = await uploadFile(imageFile, fileName);
          dataToSave.image = imageUrl;

          // If editing and there was an old image, delete it
          if (editingTestimonial && editingTestimonial.image && editingTestimonial.image.includes('firebase')) {
            try {
              const oldImagePath = editingTestimonial.image.split('/o/')[1]?.split('?')[0];
              if (oldImagePath) {
                await deleteFile(decodeURIComponent(oldImagePath));
              }
            } catch (err) {
              console.log('Could not delete old image:', err);
            }
          }
        } catch (uploadError) {
          console.error('Storage upload failed:', uploadError);
          // alert('Firebase Storage is not configured. Please enter an image URL instead or configure Firebase Storage CORS settings.');
          // return;
          console.warn('Proceeding without image upload due to storage error.');
        }
      }

      if (editingTestimonial) {
        await updateDocument('testimonials', editingTestimonial.id, dataToSave);
      } else {
        await addDocument('testimonials', dataToSave);
      }
      fetchTestimonials();
      resetForm();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      alert('Error saving testimonial: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        // If we are editing the item being deleted, close the form
        if (editingTestimonial && editingTestimonial.id === id) {
          resetForm();
        }

        const testimonial = testimonials.find(t => t.id === id);

        // Delete the image from storage if it exists
        if (testimonial && testimonial.image && testimonial.image.includes('firebase')) {
          try {
            const imagePath = testimonial.image.split('/o/')[1]?.split('?')[0];
            if (imagePath) {
              await deleteFile(decodeURIComponent(imagePath));
            }
          } catch (err) {
            console.log('Could not delete image:', err);
          }
        }

        await deleteDocument('testimonials', id);
        fetchTestimonials();
      } catch (error) {
        console.error('Error deleting testimonial:', error);
      }
    }
  };

  const handleAddNew = () => {
    setFormData({
      name: '',
      position: '',
      company: '',
      message: '',
      rating: 5,
      image: '',
      active: true,
      order: 1
    });
    setEditingTestimonial(null);
    setImageFile(null);
    setImagePreview('');
    setShowForm(true);
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      position: testimonial.position,
      company: testimonial.company,
      message: testimonial.message,
      rating: testimonial.rating,
      image: testimonial.image || '',
      active: testimonial.active,
      order: testimonial.order
    });
    setImagePreview(testimonial.image || '');
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      position: '',
      company: '',
      message: '',
      rating: 5,
      image: '',
      active: true,
      order: 1
    });
    setEditingTestimonial(null);
    setShowForm(false);
    setImageFile(null);
    setImagePreview('');
  };

  if (loading) return <Loader />;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Testimonials</h1>
          <p>Manage client testimonials</p>
        </div>
        <Button onClick={showForm ? resetForm : handleAddNew}>
          {showForm ? 'Cancel' : '+ Add Testimonial'}
        </Button>
      </div>

      {showForm && (
        <motion.div
          className="admin-form-card"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h3>{editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Position</label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Company</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Message *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows="5"
                required
                placeholder="Client testimonial message"
              />
            </div>
            <div className="form-group">
              <label>Rating *</label>
              <select
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                required
              >
                <option value={5}>5 Stars</option>
                <option value={4}>4 Stars</option>
                <option value={3}>3 Stars</option>
                <option value={2}>2 Stars</option>
                <option value={1}>1 Star</option>
              </select>
            </div>
            <div className="form-group">
              <label>Image URL (Optional)</label>
              <input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={formData.image}
                onChange={(e) => {
                  setFormData({ ...formData, image: e.target.value });
                  setImagePreview(e.target.value);
                }}
              />
              <small style={{ color: '#666', fontSize: '0.85rem' }}>
                Enter an image URL (recommended) or upload a file below (max 500KB)
              </small>
            </div>
            <div className="form-group">
              <label>Or Upload Image (max 500KB)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" style={{ maxWidth: '200px', marginTop: '1rem', borderRadius: '8px' }} />
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="form-actions">
              <Button type="submit">{editingTestimonial ? 'Update' : 'Create'} Testimonial</Button>
              <Button variant="secondary" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="admin-page-content">
        {testimonials.length === 0 ? (
          <div className="empty-state-card">
            <p>💬 No testimonials yet. Click "Add Testimonial" to create one.</p>
          </div>
        ) : (
          <div className="admin-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                className="admin-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                {testimonial.image && (
                  <div className="portfolio-card-image">
                    <img src={testimonial.image} alt={testimonial.name} />
                  </div>
                )}
                <div className="portfolio-card-content">
                  <div className="admin-card-header">
                    <h3>{testimonial.name}</h3>
                    <span className={`status-badge ${testimonial.active ? 'active' : 'inactive'}`}>
                      {testimonial.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="portfolio-meta">
                    <span className="client-name">
                      {testimonial.position}
                      {testimonial.position && testimonial.company && ' at '}
                      {testimonial.company}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.75rem', color: '#fbbf24' }}>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <FaStar key={i} />
                    ))}
                  </div>
                  <p>{testimonial.message.substring(0, 100)}{testimonial.message.length > 100 ? '...' : ''}</p>
                  <div className="admin-card-footer">
                    <span>Order: {testimonial.order}</span>
                    <div className="admin-card-actions">
                      <button onClick={() => handleEdit(testimonial)} className="btn-edit">Edit</button>
                      <button onClick={() => handleDelete(testimonial.id)} className="btn-delete">Delete</button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTestimonials;
